import { useStable } from '@/hooks/useStable';
import { ClassNameProvider } from '@/styles/ElementSliderSwiper';
import { DefaultComponent, StandaloneComponent, StandaloneComponentProps } from '@/types/component';
import { standaloneComponent } from 'modules/dynamic-components';
import { ReactNode, useCallback, useRef, useState } from 'react';
import SwiperReact, { Autoplay, Navigation } from 'swiper';
import { Swiper } from './Swiper';

export interface StandaloneSwiperProps extends StandaloneComponentProps<typeof Swiper> {
  arrow?: ReactNode;
  content?: ReactNode;
  hideNavigation?: boolean;
  items?: any[];
  title?: ReactNode;
}

export const StandaloneSwiper: StandaloneComponent<StandaloneSwiperProps> = ({
  arrow,
  content,
  items,
  hideNavigation,
  title,
  options,
  ...props
}) => {
  const {
    $group,
    $arrow,
    $content,
    $title,
    $navigation: { $item = {}, ...$navigation } = {},
    ...$base
  } = useStable(options);

  const prevElRef = useRef<HTMLDivElement>(null);
  const nextElRef = useRef<HTMLDivElement>(null);

  const [curSlide, setCurSlide] = useState(0);

  const onSlideChange = useCallback((swiper: SwiperReact) => {
    setCurSlide(swiper.realIndex);
  }, []);

  const provider = useStable({
    value: $base,
    options: {
      fallbacks: {
        size: 'variant',
        colors: 'variant',
      } as const,
    },
  });

  return (
    <ClassNameProvider {...provider}>
      <Swiper.Group {...$group}>
        <Swiper.Arrow data-direction="prev" ref={prevElRef} {...$arrow}>
          {arrow}
        </Swiper.Arrow>
        <Swiper.Content {...$content}>
          {title && <Swiper.Title {...$title}>{title}</Swiper.Title>}
          <Swiper
            modules={[Autoplay, Navigation]}
            navigation={{ prevEl: prevElRef.current, nextEl: nextElRef.current }}
            {...(!hideNavigation && { onSlideChange })}
            {...$base}
            {...props}
          >
            {content}
          </Swiper>
        </Swiper.Content>
        <Swiper.Arrow data-direction="next" ref={nextElRef} {...$arrow}>
          {arrow}
        </Swiper.Arrow>
      </Swiper.Group>
      {!hideNavigation && (
        <Swiper.Navigation {...$navigation}>
          {items?.map((_, index) => (
            <Swiper.Navigation.Item key={index} data-active={index === curSlide} {...$item} />
          ))}
        </Swiper.Navigation>
      )}
    </ClassNameProvider>
  );
};

export const standalone = StandaloneSwiper;

const DynamicStandaloneSwiper = standaloneComponent('ElementSliderSwiper') as typeof standalone;

const DefaultSwiper: DefaultComponent = () => <DynamicStandaloneSwiper />;

export default DefaultSwiper;
